import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { APP_VERSION } from "@/version";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("contact@businesstemple.co");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/agenda");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const resetPassword = async () => {
    setErr(""); setResetMsg("");
    if (!email) { setErr("Saisissez votre email ci-dessus pour réinitialiser votre mot de passe."); return; }
    setResetLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { email });
      setResetMsg(data.message || "Si un compte existe, un nouveau mot de passe vient d'être envoyé.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setResetLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-slate-100 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl" />
        <div className="flex flex-col items-start gap-2 relative">
          <img src="/soin-xpower-logotype-blanc.png" alt="Soin xPower" className="h-10 w-auto object-contain" />
          <div className="text-sm text-slate-400">Gérer sa patientèle pour professionnel de santé indépendant</div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-bold leading-tight">Votre cabinet<br /><span className="text-emerald-400">dans votre poche.</span></h1>
          <p className="mt-4 text-slate-400 max-w-md">Soignez, le reste suit : vos soirées vous appartiennent à nouveau.<br />Gestion centralisée avec l'IA : agenda et rdv, tournées, patients, paiements et automatisations des tâches fastidieuses.</p>
        </div>
        <div className="text-xs text-slate-500 relative">Serveur HDS et données sécurisées<br /><a href="https://BusinessTemple.co/webapp-soinxpower" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-emerald-400 transition-colors">Une application BusinessTemple.co</a><br /><span data-testid="app-version" className="text-slate-600">Version v{APP_VERSION}</span></div>
      </div>

      <div className="flex items-center justify-center p-6 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm sx-fade-in" data-testid="login-form">
          <h2 className="font-display text-2xl font-bold text-slate-900">Connexion</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Accédez à votre espace praticien.</p>
          <label className="text-sm font-medium text-slate-700">Email</label>
          <input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email"
            className="w-full mt-1 mb-4 px-3 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-400 outline-none" />
          <label className="text-sm font-medium text-slate-700">Mot de passe</label>
          <input data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password"
            className="w-full mt-1 mb-4 px-3 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-400 outline-none" />
          {err && <div data-testid="login-error" className="text-sm text-red-600 mb-3">{err}</div>}
          {resetMsg && <div data-testid="reset-message" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">{resetMsg}</div>}
          <button data-testid="login-submit" disabled={loading}
            className="w-full py-2.5 rounded-lg bg-[#141821] hover:bg-[#232833] text-white font-semibold transition-colors disabled:opacity-60">
            {loading ? "Connexion…" : "Se connecter"}
          </button>
          <button type="button" data-testid="reset-password-btn" onClick={resetPassword} disabled={resetLoading}
            className="w-full mt-3 text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline transition-colors disabled:opacity-60">
            {resetLoading ? "Envoi en cours…" : "Réinitialiser mon mot de passe"}
          </button>
        </form>
      </div>
    </div>
  );
}
